import { DragIndicator } from "@mui/icons-material";
import { Box, Chip } from "@mui/joy";

import { dropIndicatorSx, useSortableItem } from "./sortable";
import VisibleToClimbersIcon from "./VisibleToClimbersIcon";

type LabelChipProps = {
  uuid: string;
  name: string;
  // Shows the eye. Only root labels have their own visibility.
  isPublic?: boolean;
  // The list this chip can be reordered within.
  scope: string;
  reordering?: boolean;
  // Gets the chip's element, to anchor a menu or popover to.
  onClick?: (anchor: HTMLElement) => void;
};

export default function LabelChip({
  uuid,
  name,
  isPublic,
  scope,
  reordering = false,
  onClick,
}: LabelChipProps) {
  const { ref, isDragging, edge } = useSortableItem<HTMLSpanElement>({
    scope,
    id: uuid,
    axis: "horizontal",
    enabled: reordering,
    previewShape: "pill",
  });

  return (
    <Box
      ref={ref}
      component="span"
      sx={{
        display: "inline-flex",
        borderRadius: 999,
        opacity: isDragging ? 0.4 : 1,
        cursor: reordering ? "grab" : undefined,
        ...dropIndicatorSx(edge, "horizontal"),
        // Reaches into the gaps between chips, so a drop between two still
        // lands on one of them.
        ...(reordering && {
          "&::after": { content: '""', position: "absolute" as const, inset: "-4px" },
        }),
      }}
    >
      <Chip
        size="lg"
        variant="outlined"
        color={reordering ? "primary" : "neutral"}
        onClick={!reordering && onClick ? () => onClick(ref.current!) : undefined}
        startDecorator={
          reordering ? (
            <DragIndicator fontSize="small" />
          ) : isPublic ? (
            <VisibleToClimbersIcon />
          ) : undefined
        }
      >
        {name}
      </Chip>
    </Box>
  );
}
